import React, { useState } from 'react';
import { ArrowLeft, Map, LayoutGrid } from 'lucide-react';
import { useJourneyNavigation, JOURNEY_VIEWS } from './hooks/useJourneyNavigation';
import JourneyMapView from './components/JourneyMapView';
import UnifiedArchitectureMap from './components/UnifiedArchitectureMap';
import PillarDrilldown from './components/PillarDrilldown';
import FunctionMaturityView from './components/FunctionMaturityView';
import MaturityLevelDiagram from './components/MaturityLevelDiagram';
import JourneyAssessment from './components/JourneyAssessment';
import JourneyGapAnalysis from './components/JourneyGapAnalysis';
import JourneyBreadcrumbs from './components/JourneyBreadcrumbs';
import './ZeroTrustJourney.css';
import './components/UnifiedArchitectureMap.css';
import './components/JourneyMapView.css';

/**
 * ZeroTrustJourney Component
 *
 * Main container for the maturity-driven architecture assessment tool.
 * Provides visual navigation through CISA Zero Trust maturity levels
 * with interactive architecture diagrams and self-assessment capabilities.
 */
function ZeroTrustJourney({
  departmentId,
  assessments,
  targetLevels,
  productAdoptions,
  onUpdateLevel,
  onUpdateTargetLevel,
  onAdoptProduct,
  onRemoveProduct
}) {
  const navigation = useJourneyNavigation();

  const {
    view,
    selectedPillar,
    selectedFunction,
    selectedLevel,
    assessmentPhase,
    breadcrumbs,
    canGoBack,
    goBack,
    reset,
    selectPillar,
    selectFunction,
    viewLevel,
    startAssessment,
    selectTargetPhase,
    viewGapAnalysis,
    navigateToBreadcrumb
  } = navigation;

  /**
   * Render the current view based on navigation state
   */
  const renderView = () => {
    switch (view) {
      case JOURNEY_VIEWS.REFERENCE:
        return (
          <JourneyMapView
            departmentId={departmentId}
            assessments={assessments}
            targetLevels={targetLevels}
            productAdoptions={productAdoptions}
            onUpdateLevel={onUpdateLevel}
            onUpdateTargetLevel={onUpdateTargetLevel}
            onAdoptProduct={onAdoptProduct}
            onRemoveProduct={onRemoveProduct}
          />
        );

      case JOURNEY_VIEWS.PILLAR:
        return (
          <PillarDrilldown
            pillarCode={selectedPillar}
            assessments={assessments}
            targetLevels={targetLevels}
            onSelectFunction={selectFunction}
            onBack={goBack}
          />
        );

      case JOURNEY_VIEWS.FUNCTION:
        return (
          <FunctionMaturityView
            functionCode={selectedFunction}
            pillarCode={selectedPillar}
            currentLevel={assessments[selectedFunction] || 'TRADITIONAL'}
            targetLevel={targetLevels[selectedFunction]}
            onViewLevel={viewLevel}
            onStartAssessment={startAssessment}
            onViewGapAnalysis={viewGapAnalysis}
            onSetCurrentLevel={(level) => onUpdateLevel(selectedFunction, level)}
            onSetTargetLevel={(level) => onUpdateTargetLevel(selectedFunction, level)}
            onBack={goBack}
          />
        );

      case JOURNEY_VIEWS.LEVEL:
        return (
          <MaturityLevelDiagram
            functionCode={selectedFunction}
            level={selectedLevel}
            currentLevel={assessments[selectedFunction] || 'TRADITIONAL'}
            targetLevel={targetLevels[selectedFunction]}
            onBack={goBack}
            onStartAssessment={startAssessment}
          />
        );

      case JOURNEY_VIEWS.ASSESSMENT:
        return (
          <JourneyAssessment
            functionCode={selectedFunction}
            pillarCode={selectedPillar}
            currentLevel={assessments[selectedFunction] || 'TRADITIONAL'}
            targetLevel={targetLevels[selectedFunction]}
            assessmentPhase={assessmentPhase}
            onSelectLevel={(level) => {
              if (assessmentPhase === 'CURRENT') {
                onUpdateLevel(selectedFunction, level);
                selectTargetPhase();
              } else {
                onUpdateTargetLevel(selectedFunction, level);
                viewGapAnalysis();
              }
            }}
            onBack={goBack}
            onSkipToGap={viewGapAnalysis}
          />
        );

      case JOURNEY_VIEWS.GAP_ANALYSIS:
        return (
          <JourneyGapAnalysis
            functionCode={selectedFunction}
            pillarCode={selectedPillar}
            currentLevel={assessments[selectedFunction] || 'TRADITIONAL'}
            targetLevel={targetLevels[selectedFunction] || 'OPTIMAL'}
            productAdoptions={productAdoptions}
            onBack={goBack}
            onStartAssessment={() => startAssessment('CURRENT')}
          />
        );

      default:
        return (
          <JourneyMapView
            departmentId={departmentId}
            assessments={assessments}
            targetLevels={targetLevels}
            productAdoptions={productAdoptions}
            onUpdateLevel={onUpdateLevel}
            onUpdateTargetLevel={onUpdateTargetLevel}
            onAdoptProduct={onAdoptProduct}
            onRemoveProduct={onRemoveProduct}
          />
        );
    }
  };

  return (
    <div className="ztj-container">
      {/* Header */}
      <div className="ztj-header">
        <div className="ztj-header-left">
          {canGoBack && (
            <button
              type="button"
              className="ztj-back-btn"
              onClick={goBack}
              title="Go back"
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <div className="ztj-header-title">
            <Map size={18} />
            <span>Zero Trust Journey</span>
          </div>
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <JourneyBreadcrumbs
            crumbs={breadcrumbs}
            onNavigate={navigateToBreadcrumb}
          />
        )}

        <div className="ztj-header-right">
          {view !== JOURNEY_VIEWS.REFERENCE && (
            <button
              type="button"
              className="ztj-reset-btn"
              onClick={reset}
              title="Start over"
            >
              Start Over
            </button>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="ztj-content">
        {renderView()}
      </div>
    </div>
  );
}

export default ZeroTrustJourney;
